/**
 * WhatsApp Message Templates for NurinV2
 *
 * Bilingual templates (Malay/English) for healthcare marketing.
 * Variables use {name}, {branch}, etc. format.
 */

export interface WhatsAppTemplate {
  id: string
  name: string
  icon: string
  message: string
  description: string
}

/**
 * Format name to Title Case (proper capitalization)
 * "SITI AMINAH BINTI AHMAD" → "Siti Aminah Binti Ahmad"
 * "john doe" → "John Doe"
 */
export function formatProperName(name: string | null | undefined): string {
  if (!name) return ''

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (word.length === 0) return ''
      // Handle common Malay prefixes that should stay lowercase
      if (['bin', 'binti', 'a/l', 'a/p'].includes(word)) {
        return word
      }
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
    .trim()
}

/**
 * Template variable substitution
 *
 * @param template - Template string with {variables}
 * @param variables - Key-value pairs to substitute
 * @returns Message with variables replaced
 */
export function applyTemplateVariables(
  template: string,
  variables: Record<string, string | null | undefined>
): string {
  let result = template
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\{${key}\\}`, 'g')
    // Apply proper name formatting for 'name' variable
    const formattedValue = key === 'name' ? formatProperName(value) : (value || '')
    result = result.replace(regex, formattedValue)
  }
  // Clean up any remaining unreplaced variables
  result = result.replace(/\{[^}]+\}/g, '')
  return result.trim()
}

/**
 * Pre-defined WhatsApp templates for healthcare marketing
 */
export const WHATSAPP_TEMPLATES: WhatsAppTemplate[] = [
  {
    id: 'new-year-2026',
    name: 'New Year 2026',
    icon: '🎉',
    description: 'Ucapan Tahun Baru 2026',
    message: `Selamat Tahun Baru 2026, {name}! 🎉

Dari kami di Klinik Amisi {branch}, kami doakan tahun ini membawa kesihatan dan kebahagiaan untuk anda dan keluarga.

Terima kasih kerana menjadi sebahagian daripada keluarga Klinik Amisi. Kami sentiasa di sini untuk anda! 💆‍♀️

Selamat Tahun Baru!
- Team Klinik Amisi`,
  },
  {
    id: 'birthday',
    name: 'Birthday Wish',
    icon: '🎂',
    description: 'Ucapan Hari Lahir',
    message: `Selamat Hari Lahir, {name}! 🎂🎈

Dari kami di Klinik Amisi {branch}, semoga panjang umur, murah rezeki dan sentiasa sihat!

Sebagai hadiah istimewa, kami ada surprise untuk anda. Jom singgah ke klinik kami! 🎁

Warmest wishes,
- Team Klinik Amisi`,
  },
  {
    id: 'treatment-followup',
    name: 'Treatment Follow-up',
    icon: '💆',
    description: 'Follow-up selepas rawatan',
    message: `Hi {name}! 👋

Kami dari Klinik Amisi {branch}. Macam mana keadaan selepas treatment hari tu?

Kami harap anda berpuas hati dengan rawatan yang diterima. Kalau ada apa-apa soalan atau concern, jangan segan hubungi kami!

Ada apa-apa kami boleh bantu? 😊

- Team Klinik Amisi`,
  },
  {
    id: 'package-reminder',
    name: 'Package Reminder',
    icon: '📦',
    description: 'Peringatan pakej hampir tamat',
    message: `Hi {name}! 📦

Kami dari Klinik Amisi {branch}. Kami nak ingatkan bahawa pakej treatment anda masih ada baki session yang belum digunakan.

Jom book appointment untuk continue journey kesihatan anda! Kami tak mahu anda terlepas manfaat penuh pakej tersebut.

Hubungi kami untuk set appointment:
📞 Atau balas mesej ini terus!

- Team Klinik Amisi`,
  },
  {
    id: 'appointment-reminder',
    name: 'Appointment Reminder',
    icon: '📅',
    description: 'Peringatan temujanji',
    message: `Hi {name}! 📅

Ini reminder mesra dari Klinik Amisi {branch} - anda ada appointment dengan kami esok!

📍 Lokasi: Klinik Amisi {branch}
⏰ Sila tiba 10-15 minit awal

Kalau ada sebarang perubahan, sila maklumkan kami. See you soon! 👋

- Team Klinik Amisi`,
  },
  {
    id: 'miss-you',
    name: 'We Miss You',
    icon: '💕',
    description: 'Untuk patient yang lama tak datang',
    message: `Hi {name}! 💕

Kami dari Klinik Amisi {branch}. Lama tak dengar khabar dari anda!

Kami rindu nak jumpa anda di klinik. Macam mana keadaan sekarang? Kalau ada apa-apa kami boleh bantu untuk kesihatan dan kecantikan anda, kami sentiasa ready!

Jom visit kami semula. Ada banyak treatment baru yang menarik untuk anda! ✨

- Team Klinik Amisi`,
  },
  {
    id: 'promo',
    name: 'Special Promo',
    icon: '🔥',
    description: 'Promosi khas',
    message: `Hi {name}! 🔥

PROMO KHAS dari Klinik Amisi {branch}!

Kami ada tawaran istimewa untuk anda sebagai pelanggan setia kami. Hubungi kami untuk ketahui lebih lanjut!

Offer ni limited time je. Jangan lepaskan! 💫

- Team Klinik Amisi`,
  },
  {
    id: 'thank-you',
    name: 'Thank You',
    icon: '🙏',
    description: 'Ucapan terima kasih',
    message: `Hi {name}! 🙏

Terima kasih kerana singgah ke Klinik Amisi {branch} hari ini!

Kami harap anda berpuas hati dengan rawatan yang diterima. Kesihatan dan keselesaan anda adalah keutamaan kami.

Kalau ada apa-apa feedback atau soalan, jangan segan hubungi kami. Jumpa lagi! 💆‍♀️

- Team Klinik Amisi`,
  },
]

/**
 * Get template by ID
 */
export function getTemplateById(id: string): WhatsAppTemplate | undefined {
  return WHATSAPP_TEMPLATES.find((t) => t.id === id)
}
